import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import DataTable from "@/components/DataTable";
import { toast } from "sonner";
import { DownloadSimpleIcon, ArchiveIcon, ArrowsClockwiseIcon } from "@phosphor-icons/react";

export default function SecuritySettingsPage() {
  const { user } = useAuth();
  const [archives, setArchives] = useState({ files: [], last_run: null, retention_days: 365 });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/audit-logs/archives");
      // /audit-logs/archives currently returns a bare `[]`, not the
      // {files, last_run, retention_days} shape this page expects — guard
      // rather than overwrite the safe default and crash on
      // `archives.files.length`.
      if (data && !Array.isArray(data)) setArchives(data);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); }, []);

  const runArchiveNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/audit-logs/archives/run-now");
      toast.success(`Archived ${data.rows_archived} rows across ${data.months_archived} month${data.months_archived === 1 ? "" : "s"}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const downloadArchive = async (file) => {
    try {
      const res = await api.get(`/audit-logs/archives/${file.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = file.filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  if (user?.role !== "admin") {
    return (
      <AppLayout>
        <div className="px-8 py-10 text-sm text-[#475569]">
          Admins only. <Link to="/" className="text-[#0176D3] hover:underline">Back to dashboard</Link>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="trk-sticky-header sticky top-0 z-20 px-8 py-4">
        <div className="trk-uppercase-label">Settings</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-[#0F172A]">Security & compliance</h1>
      </div>

      <div className="px-8 py-8 max-w-4xl trk-fade-in space-y-8">
        {/* Admin 2FA enforcement card removed — the feature is hidden
            app-wide for now (see TWO_FA_ENABLED in AuthContext.jsx). */}

        {/* Audit archive retention */}
        <section className="border border-border rounded-md bg-white overflow-hidden" data-testid="audit-retention-card">
          <div className="p-6 border-b border-border">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-md bg-[#EFF6FF] flex items-center justify-center">
                <ArchiveIcon size={22} weight="fill" color="#0176D3" />
              </div>
              <div className="flex-1">
                <div className="font-display text-lg font-bold text-[#0F172A]">Audit log retention</div>
                <p className="text-sm text-[#475569] mt-1">
                  Rows older than <span className="font-mono">{archives.retention_days} days</span> are auto-compressed into a
                  monthly <code className="bg-zinc-100 px-1 py-0.5 rounded text-xs font-mono">.ndjson.gz</code> archive on the
                  1st of every month at 03:00 UTC. Nothing is ever deleted — archives are stored in the database and downloadable below.
                </p>
              </div>
              <Button onClick={runArchiveNow} disabled={busy} data-testid="archive-run-now-btn"
                      className="bg-[#0176D3] hover:bg-[#005FB2] text-white h-9 rounded-md">
                <ArrowsClockwiseIcon size={14} className="mr-1.5" /> Run now
              </Button>
            </div>
            {archives.last_run && (
              <div className="mt-4 text-xs text-[#475569] font-mono" data-testid="archive-last-run">
                Last run: {archives.last_run.ran_at?.slice(0, 19).replace("T", " ")} · archived {archives.last_run.rows_archived} row(s) across {archives.last_run.months_archived} month(s)
              </div>
            )}
          </div>

          <DataTable
            title="Archives"
            storageKey="auditArchives.visibleColumns"
            testIdPrefix="archive"
            rows={archives.files}
            rowKey={(f) => f.id}
            pageSize={20}
            emptyTestId="archives-empty"
            emptyMessage={`No archives yet. When you have audit rows older than ${archives.retention_days} days they'll be packed here.`}
            columns={[
              {
                key: "month", label: "Month", locked: true, cls: "px-4", weight: 120,
                cell: (f) => <span className="font-mono text-[#0F172A]">{f.month}</span>,
              },
              {
                key: "file", label: "File", cls: "px-4",
                cell: (f) => <div className="break-words font-mono text-xs text-[#475569]" title={f.filename}>{f.filename}</div>,
              },
              {
                key: "rows", label: "Rows", cls: "px-3 text-right", weight: 104,
                cell: (f) => <span className="font-mono tabular-nums">{f.row_count?.toLocaleString()}</span>,
              },
              {
                key: "size", label: "Size (gz)", cls: "px-3 text-right", weight: 112,
                cell: (f) => <span className="font-mono tabular-nums">{(f.compressed_size / 1024).toFixed(1)} KB</span>,
              },
              {
                key: "uploaded", label: "Uploaded", cls: "px-3 text-right", weight: 120,
                cell: (f) => <span className="font-mono text-xs text-[#94A3B8]">{f.uploaded_at?.slice(0, 10)}</span>,
              },
              {
                key: "download", label: "Download", head: "", hideFromPicker: true, cls: "px-3 text-right", weight: 128,
                cell: (f) => (
                  <button
                    onClick={() => downloadArchive(f)}
                    data-testid={`archive-download-${f.id}`}
                    className="inline-flex items-center gap-1 font-mono text-xs uppercase tracking-wider text-[#0176D3] hover:underline"
                  >
                    <DownloadSimpleIcon size={12} /> Download
                  </button>
                ),
              },
            ]}
          />
        </section>
      </div>
    </AppLayout>
  );
}
